import React from 'react';

export interface WitnessCandidate {
  id: string;
  name: string;
}

interface Props {
  candidates: WitnessCandidate[];
  /** Currently picked witness, or '' for none picked yet. */
  value: string;
  onPick: (userId: string) => void;
  /** The unwitnessed escape hatch. Rendered alongside the list, never instead
   *  of it — see the doc comment below. */
  onNoWitness: () => void;
  disabled?: boolean;
}

/**
 * The second-signature picker on a medication dose.
 *
 * Rendered as a flat list of one-tap buttons rather than a `<select>`. A
 * select costs two interactions (open, choose) and on a tablet at the medicine
 * trolley it costs a native picker sheet as well; the witness-present path is
 * budgeted at four interactions total and cannot afford the extra one. This is
 * the deliberate difference from IncidentCreateModal's `incident-witness-select`
 * — that flow has no interaction budget, this one does.
 *
 * The administrator is NEVER in `candidates`. The server rejects
 * self-witnessing with WITNESS_IS_ADMINISTRATOR (validate-witness.ts), so
 * offering yourself would be offering a guaranteed 400. The caller filters.
 *
 * "No witness available" sits next to the list, not behind a disclosure. A
 * lone staff member at 08:00 is a real and recurring situation; hiding the
 * path that records it honestly is what produces a fabricated witness name.
 */
const WitnessPicker: React.FC<Props> = ({ candidates, value, onPick, onNoWitness, disabled }) => {
  return (
    <div data-testid="witness-picker">
      <p className="text-sm font-medium text-gray-700 mb-1.5">
        Witnessed by <span className="text-red-500">*</span>
      </p>

      {candidates.length === 0 ? (
        <p className="text-xs text-gray-500 mb-2" data-testid="witness-picker-empty">
          No other staff are on this location&apos;s roster right now.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2 mb-2">
          {candidates.map((candidate) => {
            const selected = candidate.id === value;
            return (
              <button
                key={candidate.id}
                type="button"
                data-testid={`witness-option-${candidate.id}`}
                onClick={() => onPick(candidate.id)}
                disabled={disabled}
                aria-pressed={selected}
                className={`px-3 py-2.5 rounded-md border-2 text-sm font-medium text-left transition disabled:opacity-50 ${
                  selected
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {candidate.name}
              </button>
            );
          })}
        </div>
      )}

      <button
        type="button"
        data-testid="no-witness-button"
        onClick={onNoWitness}
        disabled={disabled}
        className="text-xs text-gray-600 underline hover:text-gray-800 disabled:opacity-50"
      >
        No witness available
      </button>
    </div>
  );
};

export default WitnessPicker;
